import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useInventoryPlanning } from '../contexts/InventoryPlanningContext';

type Props = {
  id: string;
  onClick?: () => void;
};

const ArticleIdCell: React.FC<Props> = ({ id, onClick }) => {
  const { data } = useInventoryPlanning();
  const meta = (data?.articles || []).find((a: any) => String(a.article_id) === String(id));
  const details = (data as any)?.article_details;
  const full = Array.isArray(details) ? details.find((a: any) => String(a.id) === String(id) || String(a.article_id) === String(id)) : undefined;

  const lines: Array<[string, any]> = useMemo(() => {
    const result: Array<[string, any]> = [];
    if (meta) {
      result.push(['Nom', meta.article_name]);
      if (meta.unit_volume_m3 !== undefined) result.push(['Volume (m³/u)', meta.unit_volume_m3]);
      if (meta.default_moq !== undefined) result.push(['MOQ', meta.default_moq]);
    }
    if (full) {
      Object.entries(full).forEach(([k, v]) => {
        if (['id', '_id', '__v'].includes(k)) return;
        if (result.find(([lk]) => lk === k)) return;
        result.push([k, v]);
      });
    }
    return result;
  }, [meta, full]);

  const truncatedName = meta?.article_name
    ? (meta.article_name.length > 25 ? `${meta.article_name.slice(0, 25)}…` : meta.article_name)
    : undefined;

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [isHover, setIsHover] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });

  const onEnter = () => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ left: rect.left + window.scrollX, top: rect.bottom + window.scrollY });
    setIsHover(true);
  };
  const onLeave = () => setIsHover(false);

  const hoverCard = useMemo(() => {
    if (!isHover || lines.length === 0) return null;
    return createPortal(
      <div
        style={{ position: 'absolute', left: pos.left, top: pos.top, zIndex: 9999 }}
        className="bg-white shadow-lg border rounded p-3 w-80"
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <div className="text-xs text-gray-500 mb-1">Détails de l'article</div>
        <div className="text-sm space-y-1 max-h-64 overflow-auto">
          {lines.slice(0, 20).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3">
              <span className="text-gray-600">{k}</span>
              <span className={`text-gray-900 text-right ${k === 'Nom' ? 'whitespace-normal break-words' : 'truncate max-w-[55%]'}`}>{String(v)}</span>
            </div>
          ))}
        </div>
      </div>,
      document.body
    );
  }, [isHover, lines, pos.left, pos.top]);

  return (
    <div className="inline-flex items-center gap-2">
      <button
        ref={anchorRef}
        className="text-blue-600 hover:underline text-left"
        onClick={onClick}
        title={meta?.article_name || String(id)}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        <span className="block whitespace-nowrap truncate leading-tight">{String(id)}</span>
        {truncatedName && (
          <span className="block whitespace-nowrap truncate leading-tight text-gray-700">{truncatedName}</span>
        )}
      </button>
      {hoverCard}
    </div>
  );
};

export default ArticleIdCell;


